import { gql, type QueryHookOptions, useQuery } from '@apollo/client';
import { FilterKey } from '@/pages/ValuationsV2/components/Filters/filterConfig';
import type { Country, Query, QueryCountriesWithFilterArgs } from '@/types/GraphqlSchema';
import { WEB_BASIC_SUBSCRIPTION } from '@/utils/constants';
import type { ValuationsFilters as ValuationsFiltersType } from '../../store/valuationsStore';
import { AutoCompleteComposable, type BaseOption } from './AutoCompleteExcelLikeComposable';

const COUNTRIES_WITH_FILTER_QUERY = gql`
  query countriesWithFilter(
    $companies: [String!]
    $assets: [JSON!]
    $basins: [String!]
    $nonOperator: Boolean
    $operator: Boolean
    $hasAssets: Boolean
    $hasBlocks: Boolean
    $hasLeases: Boolean
    $hasGhgEmissions: Boolean
    $hasMaTransactions: Boolean
    $hasNonModelledProduction: Boolean
    $includeHistoricalParticipants: Boolean
    $sortBy: String
    $feature: String
  ) {
    countriesWithFilter(
      companies: $companies
      assets: $assets
      basins: $basins
      nonOperator: $nonOperator
      operator: $operator
      hasAssets: $hasAssets
      hasBlocks: $hasBlocks
      hasLeases: $hasLeases
      hasGhgEmissions: $hasGhgEmissions
      hasMaTransactions: $hasMaTransactions
      hasNonModelledProduction: $hasNonModelledProduction
      includeHistoricalParticipants: $includeHistoricalParticipants
      sortBy: $sortBy
      feature: $feature
    ) {
      id
      displayName
      name
      isoCode
      geom
      region {
        id
        name
        displayName
      }
    }
  }
`;

type CountryOption = Country & BaseOption;

type CountriesProps = {
  filters: ValuationsFiltersType;
  onChange: (countries: Country[]) => void;
  context: string;
};

type CountriesQueryData = Pick<Query, 'countriesWithFilter'>;

const name = 'Country / Area';
const groupOptionKey = 'region';
const groupByName = 'Region';

const buildQueryVariables = (filters: ValuationsFiltersType): QueryCountriesWithFilterArgs => ({
  assets: filters[FilterKey.Assets] || [],
  companies: filters[FilterKey.Companies]?.map(company => company.name) || [],
  basins: filters[FilterKey.Basins]?.map(basin => basin.name) || [],
  nonOperator: false,
  operator: false,
  hasAssets: true,
  hasBlocks: false,
  hasLeases: false,
  hasGhgEmissions: false,
  hasNonModelledProduction: false,
  includeHistoricalParticipants: false,
  sortBy: 'displayName',
  feature: WEB_BASIC_SUBSCRIPTION,
});

export const CountriesFilter = ({ filters, onChange }: CountriesProps) => {
  const queryVariables = buildQueryVariables(filters);

  const queryOptions: QueryHookOptions<CountriesQueryData, QueryCountriesWithFilterArgs> = {
    variables: queryVariables,
    notifyOnNetworkStatusChange: true,
  };

  const { data } = useQuery<CountriesQueryData, QueryCountriesWithFilterArgs>(
    COUNTRIES_WITH_FILTER_QUERY,
    queryOptions,
  );

  return (
    <AutoCompleteComposable.Root<CountryOption>
      value={filters[FilterKey.Countries]}
      onChange={onChange}
      options={data?.countriesWithFilter || []}
      data-d='countries-filter'
    >
      <AutoCompleteComposable.Trigger>
        <AutoCompleteComposable.Label>{name}</AutoCompleteComposable.Label>
        <AutoCompleteComposable.NumberOfChosenOptions />
      </AutoCompleteComposable.Trigger>
      <AutoCompleteComposable.Content>
        <AutoCompleteComposable.GroupOptions
          groupOptionKey={groupOptionKey}
          groupsOpenedByDefault={false}
          groupByName={groupByName}
        />
      </AutoCompleteComposable.Content>
    </AutoCompleteComposable.Root>
  );
};
